import {
  WORKSPACE_IDS,
  WORKSPACES,
  getWorkspace,
  isConfiguredIdentity,
  readEnvIdentity,
} from './workspaces';

export const TENANT_IDS = {
  ORIGINATOR: 'originator',
  PROVIDER: 'provider',
  CUSTODIAN: 'custodian',
  PAYZEN: 'payzen',
  TRICOR: 'tricor',
  DYNAMIC: 'dynamic',
  UMB_EVAULT: 'umb-evault',
  UMB1: 'umb1',
  UMB2: 'umb2',
};

export const TENANT_GROUPS = {
  DEFAULT: 'default',
  WORKFLOW_TEST: 'workflow-test',
};

/**
 * Dedicated CEO workflow-test seed identities from
 * intain-evault/scripts/seed_umb_workflow_test.sql.
 * USER and ORG IDs are matching pairs; never mix across tenants.
 */
export const WORKFLOW_TEST_SEED_IDENTITIES = {
  [TENANT_IDS.PAYZEN]: {
    userId: '0e7a0001-1111-4111-8111-000000000002',
    orgId: '0e7a0001-1111-4111-8111-000000000001',
  },
  [TENANT_IDS.TRICOR]: {
    userId: '0e7a0002-2222-4222-8222-000000000002',
    orgId: '0e7a0002-2222-4222-8222-000000000001',
  },
  [TENANT_IDS.DYNAMIC]: {
    userId: '0e7a0003-3333-4333-8333-000000000002',
    orgId: '0e7a0003-3333-4333-8333-000000000001',
  },
  [TENANT_IDS.UMB_EVAULT]: {
    userId: '0e7a0004-4444-4444-8444-000000000002',
    orgId: '0e7a0004-4444-4444-8444-000000000001',
  },
  [TENANT_IDS.UMB1]: {
    userId: '0e7a0005-5555-4555-8555-000000000002',
    orgId: '0e7a0005-5555-4555-8555-000000000001',
  },
  [TENANT_IDS.UMB2]: {
    userId: '0e7a0006-6666-4666-8666-000000000002',
    orgId: '0e7a0006-6666-4666-8666-000000000001',
  },
};

/**
 * CRA inlines `process.env.REACT_APP_*` only on static member access.
 * Include Phase-1 keys so tenant identity lookups can reuse one env object.
 */
const DEFAULT_TENANT_ENV = {
  REACT_APP_ORIGINATOR_USER_ID: process.env.REACT_APP_ORIGINATOR_USER_ID || '',
  REACT_APP_ORIGINATOR_ORG_ID: process.env.REACT_APP_ORIGINATOR_ORG_ID || '',
  REACT_APP_PROVIDER_USER_ID: process.env.REACT_APP_PROVIDER_USER_ID || '',
  REACT_APP_PROVIDER_ORG_ID: process.env.REACT_APP_PROVIDER_ORG_ID || '',
  REACT_APP_CUSTODIAN_USER_ID: process.env.REACT_APP_CUSTODIAN_USER_ID || '',
  REACT_APP_CUSTODIAN_ORG_ID: process.env.REACT_APP_CUSTODIAN_ORG_ID || '',
  REACT_APP_INVESTOR_USER_ID: process.env.REACT_APP_INVESTOR_USER_ID || '',
  REACT_APP_INVESTOR_ORG_ID: process.env.REACT_APP_INVESTOR_ORG_ID || '',
  REACT_APP_PAYZEN_USER_ID: process.env.REACT_APP_PAYZEN_USER_ID || '',
  REACT_APP_PAYZEN_ORG_ID: process.env.REACT_APP_PAYZEN_ORG_ID || '',
  REACT_APP_TRICOR_USER_ID: process.env.REACT_APP_TRICOR_USER_ID || '',
  REACT_APP_TRICOR_ORG_ID: process.env.REACT_APP_TRICOR_ORG_ID || '',
  REACT_APP_DYNAMIC_USER_ID: process.env.REACT_APP_DYNAMIC_USER_ID || '',
  REACT_APP_DYNAMIC_ORG_ID: process.env.REACT_APP_DYNAMIC_ORG_ID || '',
  REACT_APP_UMB_EVAULT_USER_ID: process.env.REACT_APP_UMB_EVAULT_USER_ID || '',
  REACT_APP_UMB_EVAULT_ORG_ID: process.env.REACT_APP_UMB_EVAULT_ORG_ID || '',
  REACT_APP_UMB1_USER_ID: process.env.REACT_APP_UMB1_USER_ID || '',
  REACT_APP_UMB1_ORG_ID: process.env.REACT_APP_UMB1_ORG_ID || '',
  REACT_APP_UMB2_USER_ID: process.env.REACT_APP_UMB2_USER_ID || '',
  REACT_APP_UMB2_ORG_ID: process.env.REACT_APP_UMB2_ORG_ID || '',
};

function tenantFromWorkspace(workspace) {
  return {
    id: workspace.id,
    workspaceId: workspace.id,
    group: TENANT_GROUPS.DEFAULT,
    organizationName: workspace.organizationName,
    roleLabel: workspace.roleLabel,
    userDisplayName: workspace.userDisplayName,
    userEmail: workspace.userEmail,
    initials: workspace.initials,
    continueLabel: workspace.continueLabel,
    purpose: workspace.purpose,
    identitySource: 'workspace-env',
  };
}

export const DEFAULT_TENANTS = WORKSPACES.map(tenantFromWorkspace);

export const WORKFLOW_TEST_TENANTS = [
  {
    id: TENANT_IDS.PAYZEN,
    workspaceId: WORKSPACE_IDS.ORIGINATOR,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'Payzen',
    roleLabel: 'Originator',
    userDisplayName: 'Payzen Originator',
    userEmail: 'payzen.originator@umb.evault',
    initials: 'PZ',
    continueLabel: 'Continue as Payzen',
    purpose: 'Originator · Submit and certify documents for custody',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_PAYZEN_USER_ID',
    orgIdEnv: 'REACT_APP_PAYZEN_ORG_ID',
  },
  {
    id: TENANT_IDS.TRICOR,
    workspaceId: WORKSPACE_IDS.ORIGINATOR,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'Tricor',
    roleLabel: 'Originator',
    userDisplayName: 'Tricor Originator',
    userEmail: 'tricor.originator@umb.evault',
    initials: 'TR',
    continueLabel: 'Continue as Tricor',
    purpose: 'Originator · Submit and certify documents for custody',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_TRICOR_USER_ID',
    orgIdEnv: 'REACT_APP_TRICOR_ORG_ID',
  },
  {
    id: TENANT_IDS.DYNAMIC,
    workspaceId: WORKSPACE_IDS.ORIGINATOR,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'Dynamic',
    roleLabel: 'Originator',
    userDisplayName: 'Dynamic Originator',
    userEmail: 'dynamic.originator@umb.evault',
    initials: 'DY',
    continueLabel: 'Continue as Dynamic',
    purpose: 'Originator · Submit and certify documents for custody',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_DYNAMIC_USER_ID',
    orgIdEnv: 'REACT_APP_DYNAMIC_ORG_ID',
  },
  {
    id: TENANT_IDS.UMB_EVAULT,
    workspaceId: WORKSPACE_IDS.PROVIDER,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'UMB eVault',
    roleLabel: 'Provider',
    userDisplayName: 'UMB eVault Provider',
    userEmail: 'provider.umb.evault@umb.evault',
    initials: 'UE',
    continueLabel: 'Continue as UMB eVault',
    purpose: 'Provider · Review, validate and route custody candidates',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_UMB_EVAULT_USER_ID',
    orgIdEnv: 'REACT_APP_UMB_EVAULT_ORG_ID',
  },
  {
    id: TENANT_IDS.UMB1,
    workspaceId: WORKSPACE_IDS.CUSTODIAN,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'UMB1',
    roleLabel: 'Custodian',
    userDisplayName: 'UMB1 Custodian',
    userEmail: 'umb1.custodian@umb.evault',
    initials: 'U1',
    continueLabel: 'Continue as UMB1',
    purpose: 'Custodian · Make authority decisions and admit assets',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_UMB1_USER_ID',
    orgIdEnv: 'REACT_APP_UMB1_ORG_ID',
  },
  {
    id: TENANT_IDS.UMB2,
    workspaceId: WORKSPACE_IDS.CUSTODIAN,
    group: TENANT_GROUPS.WORKFLOW_TEST,
    organizationName: 'UMB2',
    roleLabel: 'Custodian',
    userDisplayName: 'UMB2 Custodian',
    userEmail: 'umb2.custodian@umb.evault',
    initials: 'U2',
    continueLabel: 'Continue as UMB2',
    purpose: 'Custodian · Make authority decisions and admit assets',
    identitySource: 'workflow-seed',
    userIdEnv: 'REACT_APP_UMB2_USER_ID',
    orgIdEnv: 'REACT_APP_UMB2_ORG_ID',
  },
];

export const DEMO_TENANTS = [...DEFAULT_TENANTS, ...WORKFLOW_TEST_TENANTS];

const TENANT_BY_ID = Object.fromEntries(
  DEMO_TENANTS.map((tenant) => [tenant.id, tenant])
);

export function isTenantId(value) {
  return Boolean(TENANT_BY_ID[value]);
}

export function getTenant(tenantId) {
  return TENANT_BY_ID[tenantId] || null;
}

export function defaultTenantIdForWorkspace(workspaceId) {
  return getWorkspace(workspaceId) ? workspaceId : null;
}

export function resolveTenantForWorkspace(workspaceId, tenantId) {
  if (tenantId) {
    const tenant = getTenant(tenantId);
    if (!tenant || tenant.workspaceId !== workspaceId) {
      return null;
    }
    return tenant;
  }
  return getTenant(defaultTenantIdForWorkspace(workspaceId));
}

function readWorkflowTenantIdentity(tenant, env) {
  const seed = WORKFLOW_TEST_SEED_IDENTITIES[tenant.id];
  const userId = String(env[tenant.userIdEnv] || '').trim();
  const orgId = String(env[tenant.orgIdEnv] || '').trim();

  if (!userId && !orgId) {
    const seedUserId = seed?.userId || '';
    const seedOrgId = seed?.orgId || '';
    return {
      userId: seedUserId,
      orgId: seedOrgId,
      configured: isConfiguredIdentity(seedUserId, seedOrgId),
    };
  }

  return {
    userId,
    orgId,
    configured: isConfiguredIdentity(userId, orgId),
  };
}

export function readTenantIdentity(tenantId, env = DEFAULT_TENANT_ENV) {
  const tenant = getTenant(tenantId);
  if (!tenant) {
    return { userId: '', orgId: '', configured: false };
  }
  if (tenant.identitySource === 'workspace-env') {
    return readEnvIdentity(tenant.workspaceId, env);
  }
  return readWorkflowTenantIdentity(tenant, env);
}
